import React, { useState, useMemo, useContext, useEffect } from 'react';
import { useData } from '../hooks/useData.js';
import { apiFetch, getStoredToken } from '../api/index.js';
import { LoadingSpinner, Button, PageHeader, Modal, ConfirmModal, FormInput, FormSelect, StandardPageLayout } from '../components/Common.jsx';
import { Dialog, Slide, Box, IconButton, Typography, Chip } from '@mui/material';
import { DynamicIcon } from '../components/icons.jsx';
import { useNotification } from '../context/NotificationContext.jsx';
import { ThemeContext } from '../context/ThemeContext.jsx';
import TenantAppRunnerPage from './TenantAppRunnerPage.jsx';
import TenantConnectionsTab from './TenantConnectionsTab.jsx';
import TenantDataExplorerTab from './TenantDataExplorerTab.jsx';
import TenantUsersTab from './TenantUsersTab.jsx';
import TenantThemeSettings from './TenantThemeSettings.jsx';
import ConfigurationTab from './ConfigurationTab.jsx';
import RelayAgentsSettings from './RelayAgentsSettings.jsx';
import { GitBranch, ChevronDown } from 'lucide-react';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';

// ... (The TenantForm, TenantDetailsTab, AssignedAppsTab, and AssignAppModal components remain unchanged)

const TenantForm = ({ tenant, partners, plans, onSave, onCancel, currentUser }) => {
    const isPartnerAdmin = currentUser?.role === 'Partner Admin';
    const [formData, setFormData] = useState({
        name: '', adminEmail: '', partnerId: '', planId: '', domain: '', status: 'Active'
    });

    useEffect(() => {
        if (tenant) {
            setFormData({
                name: tenant.name || '',
                adminEmail: tenant.adminEmail || '',
                partnerId: tenant.partnerId || '',
                planId: tenant.planId || '',
                domain: tenant.domain || '',
                status: tenant.status || 'Active'
            });
        }
    }, [tenant]);

    const handleChange = (e) => setFormData({ ...formData, [e.target.id]: e.target.value });
    const handleSubmit = (e) => {
        e.preventDefault();
        const dataToSave = { ...formData, planId: formData.planId === '' ? null : formData.planId };
        onSave(dataToSave);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <FormInput label="Tenant Name" id="name" value={formData.name} onChange={handleChange} required />
            <FormInput label="Admin Email" id="adminEmail" type="email" value={formData.adminEmail} onChange={handleChange} required />
            <FormSelect label="Partner" id="partnerId" value={formData.partnerId} onChange={handleChange} disabled={isPartnerAdmin}>
                {partners?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </FormSelect>
            <FormSelect label="Plan" id="planId" value={formData.planId} onChange={handleChange}>
                <option value="">-- No Plan --</option>
                {plans?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </FormSelect>
            <FormInput label="Domain" id="domain" value={formData.domain} onChange={handleChange} />
            <FormSelect label="Status" id="status" value={formData.status} onChange={handleChange}>
                <option>Active</option><option>Inactive</option><option>Pending</option>
            </FormSelect>
            <div className="flex justify-end gap-4 pt-4"><Button type="button" variant="secondary" onClick={onCancel} withShadow>Cancel</Button><Button type="submit">Save Changes</Button></div>
        </form>
    );
};
const TenantDetailsTab = ({ tenant, formatDate }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-4">Tenant Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div><p className="font-semibold text-gray-500">Tenant ID</p><p className="font-mono text-xs">{tenant.id}</p></div>
            <div><p className="font-semibold text-gray-500">Admin Email</p><p>{tenant.adminEmail}</p></div>
            <div><p className="font-semibold text-gray-500">Domain</p><p>{tenant.domain || 'N/A'}</p></div>
            <div><p className="font-semibold text-gray-500">Status</p><p>{tenant.status}</p></div>
            <div><p className="font-semibold text-gray-500">Creation Date</p><p>{formatDate ? formatDate(tenant.creationDate) : tenant.creationDate}</p></div>
            <div><p className="font-semibold text-gray-500">Partner</p><p>{tenant.partnerName}</p></div>
            <div><p className="font-semibold text-gray-500">Plan</p><p>{tenant.planName || 'N/A'}</p></div>
        </div>
    </div>
);
const AssignedAppsTab = ({ tenantId, token }) => {
    const { data: assignedApps, isLoading, error, refresh } = useData(`/tenants/${tenantId}/apps`, token);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [confirmUnassign, setConfirmUnassign] = useState(null);
    const [previewApp, setPreviewApp] = useState(null);
    const { addNotification } = useNotification();

    const handleAssign = async (assignments) => {
        try {
            await Promise.all(
                assignments.map(app => apiFetch(`/tenants/${tenantId}/apps`, token, { method: 'POST', body: JSON.stringify(app) }))
            );
            addNotification('Apps assigned successfully.');
            refresh();
        } catch (err) {
            addNotification("Error assigning apps: " + err.message, 'error');
        } finally {
            setIsAssignModalOpen(false);
        }
    };

    const handleUnassign = async () => {
        if (!confirmUnassign) return;
        try {
            await apiFetch(`/tenants/${tenantId}/apps/${confirmUnassign.id}`, token, { method: 'DELETE' });
            addNotification('App unassigned successfully.');
            refresh();
        } catch (err) {
            addNotification("Error unassigning app: " + err.message, 'error');
        } finally {
            setConfirmUnassign(null);
        }
    };

    if (isLoading) return <LoadingSpinner />;
    if (error) return <div className="text-red-500 p-4 bg-red-50 rounded-md">Error loading assigned apps: {error.message}</div>;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Assigned Apps & Content</h2>
                <Button onClick={() => setIsAssignModalOpen(true)}>
                    <DynamicIcon name="PlusCircle" className="mr-2 h-4 w-4" />
                    Assign
                </Button>
            </div>
            <div className="space-y-4">
                {assignedApps?.map(app => (
                    <div key={app.id} className="flex items-center justify-between p-4 rounded-md border dark:border-gray-700">
                        <div className="flex items-center gap-4">
                            <DynamicIcon name={app.type === 'App Bundle' ? 'Package' : 'BrainCircuit'} className="w-8 h-8 text-primary" />
                            <div>
                                <p className="font-semibold text-gray-800 dark:text-gray-100">{app.name}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{app.type}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${app.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{app.status}</span>
                            <Button variant="secondary" onClick={() => setPreviewApp(app)} title="Preview as Tenant">
                                <DynamicIcon name="Eye" className="w-4 h-4" />
                            </Button>
                            <Button variant="danger" onClick={() => setConfirmUnassign(app)}>Unassign</Button>
                        </div>
                    </div>
                ))}
                {assignedApps?.length === 0 && (
                    <p className="text-center text-gray-500 dark:text-gray-400 py-8">No apps or content have been assigned to this tenant yet.</p>
                )}
            </div>
            <AssignAppModal isOpen={isAssignModalOpen} onClose={() => setIsAssignModalOpen(false)} onAssign={handleAssign} token={token} tenantId={tenantId} />
            <ConfirmModal
                isOpen={!!confirmUnassign}
                onClose={() => setConfirmUnassign(null)}
                onConfirm={handleUnassign}
                title={`Unassign ${confirmUnassign?.name}?`}
            >
                Are you sure you want to unassign this app from the tenant? Access will be revoked immediately.
            </ConfirmModal>

            {/* Preview Modal */}
            <AppPreviewModal app={previewApp} onClose={() => setPreviewApp(null)} token={token} />
        </div>
    );
};
const AssignAppModal = ({ isOpen, onClose, onAssign, token, tenantId }) => {
    const { formatDate } = useTimezoneFormatter();
    const { data: bundles } = useData('/business-apps/bundles', token);
    const { data: contents } = useData('/business-apps/contents', token);

    // Wizard State
    const [step, setStep] = useState(1);
    const [selection, setSelection] = useState(null); // { type: 'bundle'|'content', id: str }
    const [bundleDetails, setBundleDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Configuration State
    const [includeData, setIncludeData] = useState(false);
    const [scheduleConfigs, setScheduleConfigs] = useState({});
    const [modelConfigs, setModelConfigs] = useState({});

    // Version picker state for individual content items
    const [versionSelections, setVersionSelections] = useState({}); // { contentId: { version_id, version_label } }
    const [openVersionPicker, setOpenVersionPicker] = useState(null);
    const [loadedVersions, setLoadedVersions] = useState({}); // { contentId: versions[] | null(loading) }

    // Reset on Open
    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setSelection(null);
            setBundleDetails(null);
            setIncludeData(false);
            setScheduleConfigs({});
            setModelConfigs({});
            setVersionSelections({});
            setOpenVersionPicker(null);
            setLoadedVersions({});
        }
    }, [isOpen]);

    // Unified version loader — supports both flow-backed and dashboard-backed content
    const loadVersionsForContent = async (content) => {
        if (loadedVersions[content.id] !== undefined) return;
        setLoadedVersions(prev => ({ ...prev, [content.id]: null })); // null = loading
        try {
            const tkn = getStoredToken();
            let endpoint = null;
            if (content.flow_id) {
                endpoint = `/flows/${content.flow_id}/versions`;
            } else if (content.dashboard_id) {
                endpoint = `/dashboards/${content.dashboard_id}/versions`;
            }
            if (!endpoint) {
                setLoadedVersions(prev => ({ ...prev, [content.id]: [] }));
                return;
            }
            const data = await apiFetch(endpoint, tkn);
            setLoadedVersions(prev => ({ ...prev, [content.id]: data || [] }));
        } catch {
            setLoadedVersions(prev => ({ ...prev, [content.id]: [] }));
        }
    };

    // Fetch Details when moving to Step 2
    useEffect(() => {
        if (step === 2 && selection) {
            setLoadingDetails(true);
            const endpoint = selection.type === 'bundle'
                ? `/business-apps/bundles/${selection.id}`
                : `/business-apps/contents/${selection.id}`;

            apiFetch(endpoint, token)
                .then(data => {
                    setBundleDetails(data);
                    // Initialize Configs
                    const initialConfigs = {};

                    // Datasets can come from bundle contents OR content dependencies
                    const datasets = selection.type === 'bundle'
                        ? (data.contents?.filter(c => c.type === 'dataset') || [])
                        : (data.datasets || []);

                    datasets.forEach(ds => {
                        initialConfigs[ds.id] = {
                            enabled: ds.refresh_policy === 'scheduled',
                            type: 'daily',
                            time: '00:00',
                            cron: ds.refresh_cron || '0 0 * * *'
                        };
                    });
                    setScheduleConfigs(initialConfigs);

                    // Initialize Model Configs
                    const initialModelConfigs = {};
                    const models = selection.type === 'bundle'
                        ? (data.contents?.filter(c => c.type === 'content' && c.contentTypeName !== 'Dashboard') || [])
                        : (data.contentTypeName !== 'Dashboard' ? [data] : []); // For single content assignment

                    models.forEach(m => {
                        initialModelConfigs[m.id] = { enabled: false, type: 'daily', time: '00:00', cron: '0 0 * * *' };
                    });
                    setModelConfigs(initialModelConfigs);
                })
                .catch(err => console.error("Failed to load details", err))
                .finally(() => setLoadingDetails(false));
        }
    }, [step, selection, token]);

    const handleNext = () => {
        if (selection) {
            setStep(2);
        }
    };

    const handleSubmit = () => {
        const verSel = selection.type === 'content' ? versionSelections[selection.id] : null;
        // Construct Final Payload
        const assignment = {
            app_bundle_id: selection.type === 'bundle' ? selection.id : null,
            content_id: selection.type === 'content' ? selection.id : null,
            content_version_id: verSel?.version_id || null,
            content_version_label: verSel?.version_label || null,
            provision_options: { include_data: includeData },
            configurations: { datasets: {} }
        };

        // Transform Schedule Configs to Backend Format
        if (selection.type === 'bundle') {
            Object.entries(scheduleConfigs).forEach(([dsId, cfg]) => {
                let finalCron = cfg.cron;
                if (cfg.enabled) {
                    if (cfg.type === 'daily') {
                        const [h, m] = (cfg.time || '00:00').split(':');
                        finalCron = `${Number(m)} ${Number(h)} * * *`;
                    } else if (cfg.type === 'hourly') {
                        finalCron = '0 * * * *';
                    }
                }

                assignment.configurations.datasets[dsId] = {
                    refresh_policy: cfg.enabled ? 'scheduled' : 'manual',
                    refresh_cron: cfg.enabled ? finalCron : null
                };
            });
        }
        // For Content type, we effectively do the same dataset mapping if any configs exist
        // 2. Model Run Schedules (Consolidated Logic)
        Object.entries(modelConfigs).forEach(([cId, cfg]) => {
            if (cfg.enabled) {
                let runCron = cfg.cron;
                if (cfg.type === 'daily') {
                    const [h, m] = (cfg.time || '00:00').split(':');
                    runCron = `${Number(m)} ${Number(h)} * * *`;
                } else if (cfg.type === 'hourly') {
                    runCron = '0 * * * *';
                }

                // Add to 'models' dict in configurations
                if (!assignment.configurations.models) assignment.configurations.models = {};
                assignment.configurations.models[cId] = {
                    enabled: true,
                    cron: runCron
                };
            }
        });

        // Pass array (UI originally supported multiple, now wizard focuses on singleton for clarity, 
        // but prop expects array)
        onAssign([assignment]);
        onClose();
    };

    const handleModelConfigChange = (cId, field, value) => {
        setModelConfigs(prev => ({
            ...prev,
            [cId]: { ...prev[cId], [field]: value }
        }));
    };

    const handleScheduleChange = (dsId, field, value) => {
        setScheduleConfigs(prev => ({
            ...prev,
            [dsId]: { ...prev[dsId], [field]: value }
        }));
    };

    // Render Logic
    const renderSelectionStep = () => (
        <div className="space-y-4 max-h-[60vh] overflow-y-auto p-1">
            <div>
                <h3 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">App Bundles</h3>
                <div className="space-y-2">
                    {bundles?.map(bundle => (
                        <div key={bundle.id} onClick={() => setSelection({ type: 'bundle', id: bundle.id })}
                            className={`p-3 rounded-md cursor-pointer flex items-center gap-3 transition-colors ${selection?.id === bundle.id ? 'bg-primary/10 ring-2 ring-primary' : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'}`}>
                            <DynamicIcon name="Package" />
                            <div className="flex-1">
                                <p className="font-medium">{bundle.name}</p>
                                <p className="text-xs text-gray-500">{bundle.description}</p>
                                <p className="text-[10px] text-gray-400 font-mono mt-1">ID: {bundle.id}</p>
                            </div>
                            <DynamicIcon name="ChevronRight" className="text-gray-400" />
                        </div>
                    ))}
                </div>
            </div>
            <div>
                <h3 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">Individual Content</h3>
                <div className="space-y-2">
                    {contents?.map(content => {
                        const verSel = versionSelections[content.id];
                        return (
                            <div key={content.id}
                                className={`p-3 rounded-md cursor-pointer flex items-center gap-3 transition-colors ${selection?.id === content.id ? 'bg-primary/10 ring-2 ring-primary' : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
                                onClick={() => setSelection({ type: 'content', id: content.id })}>
                                <DynamicIcon name="BrainCircuit" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                        <p className="font-medium truncate">{content.name}</p>
                                        {(content.flow_id || content.dashboard_id) && (
                                            <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-300">
                                                {verSel?.version_label || content.version || 'Latest'}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        {content.flow_id ? 'Flow App' : content.dashboard_id ? 'Dashboard App' : `v${content.version}`}
                                    </p>
                                </div>

                                {/* Version Picker — for both flow-backed and dashboard-backed content */}
                                {(content.flow_id || content.dashboard_id) && (
                                    <div className="flex-shrink-0 relative" onClick={e => e.stopPropagation()}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                loadVersionsForContent(content);
                                                setOpenVersionPicker(openVersionPicker === content.id ? null : content.id);
                                            }}
                                            className="flex items-center gap-1 px-2 py-1 rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-500 dark:text-slate-400 text-xs hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                            title="Select version"
                                        >
                                            <GitBranch className="w-3 h-3" />
                                            <span>{verSel?.version_label || 'Latest'}</span>
                                            <ChevronDown className="w-3 h-3" />
                                        </button>

                                        {openVersionPicker === content.id && (
                                            <div className="absolute z-30 mt-1 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl min-w-[170px] right-0">
                                                <div className="p-1">
                                                    {/* Latest (auto) option */}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setVersionSelections(prev => { const n = {...prev}; delete n[content.id]; return n; });
                                                            setOpenVersionPicker(null);
                                                        }}
                                                        className={`block w-full text-left px-2 py-1.5 text-xs rounded hover:bg-gray-100 dark:hover:bg-slate-800 font-medium ${
                                                            !verSel ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-slate-200'
                                                        }`}
                                                    >
                                                        Latest (auto)
                                                    </button>

                                                    {/* Loading state */}
                                                    {loadedVersions[content.id] === null && (
                                                        <div className="px-2 py-1.5 text-xs text-gray-400 italic">Loading versions…</div>
                                                    )}

                                                    {/* No versions found */}
                                                    {Array.isArray(loadedVersions[content.id]) && loadedVersions[content.id].length === 0 && (
                                                        <div className="px-2 py-1.5 text-xs text-gray-400 italic">No published versions found</div>
                                                    )}

                                                    {/* Version list */}
                                                    {(loadedVersions[content.id] || []).map(v => (
                                                        <button
                                                            key={v.id}
                                                            type="button"
                                                            onClick={() => {
                                                                setVersionSelections(prev => ({
                                                                    ...prev,
                                                                    [content.id]: { version_id: v.id, version_label: v.version || `v${v.version_number}` }
                                                                }));
                                                                setOpenVersionPicker(null);
                                                            }}
                                                            className={`block w-full text-left px-2 py-1.5 text-xs rounded hover:bg-blue-50 dark:hover:bg-slate-800 ${
                                                                verSel?.version_id === v.id
                                                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                                                                    : 'text-gray-700 dark:text-slate-200'
                                                            }`}
                                                        >
                                                            <span className="font-medium">{v.version || `v${v.version_number}`}</span>
                                                            {v.is_active && <span className="ml-1.5 text-green-500 text-[10px]">● Live</span>}
                                                            {v.published_at && (
                                                                <span className="block text-[10px] text-gray-400 mt-0.5">
                                                                    {formatDate(v.published_at, { month: 'short', day: '2-digit', year: 'numeric' })}
                                                                </span>
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    const renderConfigStep = () => {
        if (loadingDetails) return <LoadingSpinner />;
        if (!bundleDetails) return <p className="text-red-500">Failed to load details.</p>;

        const isBundle = selection.type === 'bundle';
        // Normalize: Bundle has .contents list. Content has .datasets list (added recursively)
        const datasets = isBundle
            ? (bundleDetails.contents?.filter(c => c.type === 'dataset') || [])
            : (bundleDetails.datasets || []);

        const isML = selection.type === 'content' && bundleDetails.contentTypeName !== 'Dashboard';

        // Helper to render Schedule Inputs (Reusable)
        const renderScheduleInputs = (config, onChange) => (
            <div className="mt-3 grid grid-cols-2 gap-3 pl-6 border-l-2 border-primary/20 bg-gray-50 dark:bg-gray-900/50 p-2 rounded-r">
                <div>
                    <label className="text-xs font-semibold text-gray-500">Frequency</label>
                    <select
                        value={config.type}
                        onChange={e => onChange('type', e.target.value)}
                        className="w-full mt-1 p-1 text-sm border rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    >
                        <option value="daily" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">Daily</option>
                        <option value="hourly" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">Hourly</option>
                        <option value="custom" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">Custom Cron</option>
                    </select>
                </div>

                {config.type === 'daily' && (
                    <div>
                        <label className="text-xs font-semibold text-gray-500">Time (UTC)</label>
                        <input
                            type="time"
                            value={config.time}
                            onChange={e => onChange('time', e.target.value)}
                            className="w-full mt-1 p-1 text-sm border rounded bg-white dark:bg-gray-800"
                        />
                    </div>
                )}

                {config.type === 'custom' && (
                    <div className="col-span-2">
                        <label className="text-xs font-semibold text-gray-500">Cron Expression</label>
                        <input
                            type="text"
                            value={config.cron}
                            onChange={e => onChange('cron', e.target.value)}
                            className="w-full mt-1 p-1 text-sm border rounded bg-white dark:bg-gray-800 font-mono"
                            placeholder="* * * * *"
                        />
                    </div>
                )}
            </div>
        );

        return (
            <div className="space-y-6 max-h-[65vh] overflow-y-auto pr-2">
                {/* Header Info */}
                <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <DynamicIcon name={bundleDetails.icon || (isML ? 'BrainCircuit' : 'Package')} className="w-10 h-10 text-primary" />
                    <div>
                        <h3 className="text-lg font-bold">{bundleDetails.name}</h3>
                        <p className="text-sm text-gray-500">{bundleDetails.description || 'No description'}</p>
                        <p className="text-xs text-gray-400 font-mono mt-1">ID: {bundleDetails.id}</p>
                    </div>
                </div>


                {/* Main Content Execution Schedule (For ML Models) */}
                {Object.keys(modelConfigs).length > 0 && (
                    <div className="space-y-3 mb-4">
                        <h4 className="font-semibold flex items-center gap-2"><DynamicIcon name="Play" className="w-4 h-4" /> Model Execution</h4>
                        {Object.entries(modelConfigs).map(([cId, cfg]) => {
                            // Find model name for label
                            const modelName = isBundle
                                ? bundleDetails.contents.find(c => c.id === cId)?.name
                                : bundleDetails.name;

                            return (
                                <div key={cId} className="border dark:border-gray-700 rounded-md p-3 bg-white dark:bg-gray-800">
                                    <div className="flex items-center justify-between mb-2">
                                        <div>
                                            <span className="font-medium block">{modelName}</span>
                                            <p className="text-xs text-gray-500">Schedule execution</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-gray-500 font-medium">Enable Schedule</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input type="checkbox" checked={cfg.enabled} onChange={e => handleModelConfigChange(cId, 'enabled', e.target.checked)} className="sr-only peer" />
                                                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                                            </label>
                                        </div>
                                    </div>
                                    {cfg.enabled && renderScheduleInputs(cfg, (f, v) => handleModelConfigChange(cId, f, v))}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Dependencies Tree */}
                <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <DynamicIcon name="Layers" className="w-4 h-4" /> Dependencies
                    </h4>

                    {/* 1. Datasets Configuration */}
                    {datasets.length > 0 ? (
                        <div className="mb-4 space-y-3">
                            <h5 className="text-sm font-medium text-gray-600 uppercase tracking-wider">Datasets & Refresh Schedules</h5>
                            {datasets.map(ds => {
                                const cfg = scheduleConfigs[ds.id] || { enabled: false };
                                return (
                                    <div key={ds.id} className="border dark:border-gray-700 rounded-md p-3 bg-white dark:bg-gray-800">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <DynamicIcon name="Database" className="text-blue-500 w-4 h-4" />
                                                <div>
                                                    <span className="font-medium block">{ds.name}</span>
                                                    <span className="text-[10px] text-gray-400 font-mono">ID: {ds.id}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-gray-500">Schedule Refresh</span>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input type="checkbox" checked={cfg.enabled} onChange={e => handleScheduleChange(ds.id, 'enabled', e.target.checked)} className="sr-only peer" />
                                                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                                                </label>
                                            </div>
                                        </div>

                                        {/* Schedule Inputs */}
                                        {cfg.enabled && renderScheduleInputs(cfg, (f, v) => handleScheduleChange(ds.id, f, v))}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500 italic">No dependent datasets found.</p>
                    )}
                </div>
            </div>
        );
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={step === 1 ? `Assign App (Tenant: ${tenantId})` : "Assign App: Configure"}>
            <div className="mt-2 min-h-[400px]">
                {step === 1 ? renderSelectionStep() : renderConfigStep()}
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t mt-4">
                <Button variant="secondary" onClick={step === 1 ? onClose : () => setStep(1)}>
                    {step === 1 ? "Cancel" : "Back"}
                </Button>
                <Button
                    onClick={step === 1 ? handleNext : handleSubmit}
                    disabled={!selection}
                >
                    {step === 1 ? "Review & Configure" : "Confirm Assignment"}
                </Button>
            </div>
        </Modal>
    );
};


// --- Internal Preview Component (Handles Sidebar + Navigation State) ---

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const AppPreviewModal = ({ app, onClose, token }) => {
    const [currentPath, setCurrentPath] = useState('');

    useEffect(() => {
        if (app) setCurrentPath(`/tenant/app/${app.id}`);
    }, [app]);

    const menuItems = useMemo(() => {
        if (!app || !app.contents) return [];
        const isDash   = (c) => c.type === 'dashboard' || c.content_type === 'dashboard';
        const dashboards = app.contents.filter(isDash);
        const mlContents = app.contents.filter(c => !isDash(c));
        const childrenItems = [];

        if (dashboards.length > 0) {
            childrenItems.push({
                key: `grp-insights-${app.id}`, label: 'Insights', type: 'group',
                path: `/tenant/app/${app.id}?category=dashboard`,
                children: dashboards.map(d => ({
                    key: d.id, path: `/tenant/app/${app.id}/${d.id}`, icon: 'LayoutDashboard',
                    label: d.name
                }))
            });
        }
        if (mlContents.length > 0) {
            childrenItems.push({
                key: `grp-ml-${app.id}`, label: 'ML Insights', type: 'group',
                path: `/tenant/app/${app.id}?category=ml_model`,
                children: mlContents.map(m => ({
                    key: m.id, path: `/tenant/app/${app.id}/${m.id}`, icon: 'BrainCircuit',
                    label: m.name
                }))
            });
        }
        return [{
            key: 'my_apps', label: 'Preview App', type: 'group',
            children: [{
                key: app.id, label: app.name,
                path: `/apps/${app.id}`, icon: 'LayoutGrid',
                children: childrenItems.length > 0 ? childrenItems : undefined
            }]
        }];
    }, [app]);

    // Parse contentUid from path: /tenant/app/:appId/:contentUid
    const contentUid = currentPath.split('/')[4];

    // Simple Inline Sidebar Renderer (Same as BusinessAppsPage)
    const renderSidebarItem = (item, depth = 0) => {
        if (item.type === 'group') {
            return (
                <div key={item.key} className="mb-2">
                    {item.label && <div className={`px-4 py-1 text-xs font-semibold text-gray-500 uppercase ${depth > 0 ? 'mt-2' : ''}`}>{item.label}</div>}
                    {item.children?.map(child => renderSidebarItem(child, depth + 1))}
                </div>
            );
        }

        const hasChildren = item.children && item.children.length > 0;
        const isActive = currentPath === item.path;

        return (
            <div key={item.key}>
                <div
                    onClick={() => !hasChildren && item.path && setCurrentPath(item.path)}
                    className={`mx-2 px-3 py-2 text-sm rounded-md cursor-pointer flex items-center gap-2 transition-colors
                        ${isActive ? 'bg-primary/10 text-primary font-medium' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}
                        ${depth > 0 ? 'ml-4' : ''}
                    `}
                >
                    <DynamicIcon name={item.icon || 'Circle'} className="w-4 h-4" />
                    <span className="truncate">{item.label}</span>
                </div>
                {hasChildren && (
                    <div className="ml-2 border-l border-gray-100 dark:border-gray-700">
                        {item.children.map(child => renderSidebarItem(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Dialog
            fullScreen
            open={!!app}
            onClose={onClose}
            TransitionComponent={Transition}
            PaperProps={{ sx: { bgcolor: 'background.default' } }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <DynamicIcon name="Eye" className="w-5 h-5 mr-2" />
                <Typography variant="h6" sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    Previewing: {app?.name}
                    <Chip label="tenant-view" size="small" variant="outlined" sx={{ ml: 2, height: 20, fontSize: '0.65rem' }} />
                </Typography>
                <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
                    <DynamicIcon name="X" />
                </IconButton>
            </Box>
            {app && (
                <div className="flex h-full bg-gray-50 dark:bg-gray-900">
                    <div className="w-64 flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
                        <div className="p-4 border-b border-gray-100 dark:border-gray-800">
                            <h3 className="font-bold text-gray-800 dark:text-gray-200">App Preview</h3>
                        </div>
                        <div className="py-2">
                            {menuItems.map(item => renderSidebarItem(item))}
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        <TenantAppRunnerPage
                            key={currentPath}
                            instance={app}
                            contentUid={contentUid}
                            onNavigate={setCurrentPath} // Intercepts clicks to update local state
                            token={token}
                        />
                    </div>
                </div>
            )}
        </Dialog>
    );
};

// --- Main Exported Component ---
const TenantDetailPage = ({ tenantId, onBack, token, currentUser, onLogout }) => {
    // Admin View Only
    const { formatDate } = useTimezoneFormatter();
    const { data: tenant, isLoading: isLoadingTenant, error: tenantError, refresh } = useData(
        `/tenants/${tenantId}`, token
    );

    useEffect(() => {
        if (tenant?.name) {
            window.dispatchEvent(new CustomEvent('set-breadcrumb-title', { detail: tenant.name }));
        }
    }, [tenant]);

    const { data: partners } = useData('/partners', token);
    const { data: plans } = useData('/plans', token);

    const [activeTab, setActiveTab] = useState('details');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const { addNotification } = useNotification();

    if (isLoadingTenant) return <LoadingSpinner />;
    if (tenantError) return <div className="text-red-500 p-4 rounded-md bg-red-50">Error loading tenant details: {tenantError.message}</div>;
    if (!tenant) return <h2>Tenant not found.</h2>;

    // NO TenantPortalLayout return here.

    const handleSave = async (tenantData) => {
        try {
            await apiFetch(`/tenants/${tenantId}`, token, {
                method: 'PUT',
                body: JSON.stringify(tenantData)
            });
            addNotification("Tenant details updated successfully.");
            refresh();
        } catch (err) {
            addNotification("Error updating tenant: " + err.message, 'error');
        } finally {
            setIsEditModalOpen(false);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            await apiFetch(`/tenants/${confirmDelete.id}`, token, { method: 'DELETE' });
            addNotification(`Tenant "${confirmDelete.name}" deactivated.`);
            onBack();
        } catch (err) {
            addNotification("Error deactivating tenant: " + err.message, 'error');
        } finally {
            setConfirmDelete(null);
        }
    };

    const headerActions = (
        <div className="flex items-center gap-2">
            <Button onClick={onBack} variant="secondary" sx={{ height: 32 }}>
                <DynamicIcon name="ChevronLeft" className="w-4 h-4 mr-2" />
                All Tenants
            </Button>
            <Button onClick={() => setIsEditModalOpen(true)} variant="secondary" sx={{ height: 32 }}>
                <DynamicIcon name="Edit" className="w-4 h-4 mr-2" />
                Edit
            </Button>
            <Button onClick={() => setConfirmDelete(tenant)} variant="danger" sx={{ height: 32 }}>
                <DynamicIcon name="Trash2" className="w-4 h-4 mr-2" />
                Deactivate
            </Button>
        </div>
    );

    return (
        <StandardPageLayout
            title={tenant.name || 'Tenant Details'}
            description="View and manage tenant details and settings."
            headerActions={headerActions}
        >

            <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                <nav className="-mb-px flex space-x-8">
                    <button onClick={() => setActiveTab('details')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'details' ? 'border-primary text-primary' : 'border-transparent text-gray-500'}`}>Details</button>
                    <button onClick={() => setActiveTab('apps')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'apps' ? 'border-primary text-primary' : 'border-transparent text-gray-500'}`}>Assigned Apps</button>
                    <button onClick={() => setActiveTab('users')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'users' ? 'border-primary text-primary' : 'border-transparent text-gray-500'}`}>Users</button>
                    <button onClick={() => setActiveTab('config')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'config' ? 'border-primary text-primary' : 'border-transparent text-gray-500'}`}>Configuration</button>
                    <button onClick={() => setActiveTab('relay')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'relay' ? 'border-primary text-primary' : 'border-transparent text-gray-500'}`}>Hybrid Agents</button>
                </nav>
            </div>

            <div>
                {activeTab === 'details' && <TenantDetailsTab tenant={tenant} formatDate={formatDate} />}
                {activeTab === 'apps' && <AssignedAppsTab tenantId={tenantId} token={token} />}
                {activeTab === 'users' && <TenantUsersTab token={token} currentUser={currentUser} tenantId={tenantId} tenantDomain={tenant.domain || (tenant.adminEmail ? tenant.adminEmail.split('@')[1] : '')} />}
                {activeTab === 'config' && <ConfigurationTab tenantId={tenantId} token={token} currentUser={currentUser} />}
                {activeTab === 'relay' && <RelayAgentsSettings tenantId={tenantId} token={token} />}
            </div>

            <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title={`Edit ${tenant.name}`}>
                <TenantForm tenant={tenant} partners={partners || []} plans={plans || []} onSave={handleSave} onCancel={() => setIsEditModalOpen(false)} currentUser={currentUser} />
            </Modal>
            <ConfirmModal
                isOpen={!!confirmDelete}
                onClose={() => setConfirmDelete(null)}
                onConfirm={handleDelete}
                title={`Deactivate ${confirmDelete?.name}?`}
            >
                Are you sure you want to deactivate this tenant? This action will disable access for all their users.
            </ConfirmModal>
        </StandardPageLayout>
    );
};

export default TenantDetailPage;